import { ENV } from '@/constants';
import { exploreSchemaItemList } from '../exploreSchema';

export const schemaObject_PixverseAiVideoGenerator = {
	'@context': 'https://schema.org',
	'@graph': [
		{
			'@type': 'WebPage',
			'@id': 'https://liftapp.ai/pixverse-ai-video-generator',
			name: 'PixVerse AI Video Generator — Multi-Shot With Sound by Lift',
			description:
				'Turn a prompt into a multi-shot video with built-in audio using the PixVerse model inside Lift. Describe it, pick a ratio, and download. Try Lift today!',
			url: 'https://liftapp.ai/pixverse-ai-video-generator',
			image: 'https://liftapp.ai/seo-image.webp',
			mainEntity: [
				{ '@id': 'https://liftapp.ai/pixverse-ai-video-generator#product' },
				{ '@id': 'https://liftapp.ai/pixverse-ai-video-generator#howto' },
				{ '@id': 'https://liftapp.ai/pixverse-ai-video-generator#benefits' },
				{ '@id': 'https://liftapp.ai/pixverse-ai-video-generator#faq' },
				{ '@id': 'https://liftapp.ai/pixverse-ai-video-generator#why-lift' },
				{
					'@id': 'https://liftapp.ai/pixverse-ai-video-generator#explore-tools',
				},
				{ '@id': 'https://liftapp.ai/pixverse-ai-video-generator#all-in-one' },
			],
		},
		// product
		{
			'@type': 'Product',
			'@id': 'https://liftapp.ai/pixverse-ai-video-generator#product',
			name: 'PixVerse AI Video Generator',
			description:
				'Turn a prompt into a multi-shot video with built-in audio using the PixVerse model inside Lift. Describe it, pick a ratio, and download.',
			url: 'https://liftapp.ai/pixverse-ai-video-generator',
			fileFormat: ['video/mp4'],
			brand: {
				'@type': 'Brand',
				name: 'Lift',
			},
			aggregateRating: {
				'@type': 'AggregateRating',
				ratingValue: '4.8',
				reviewCount: 130000,
				bestRating: '5',
				worstRating: '1',
			},
			interactionStatistic: [
				{
					'@type': 'InteractionCounter',
					interactionType: { '@type': 'InstallAction' },
					userInteractionCount: 13000000,
				},
			],
		},
		// howto
		{
			'@type': 'HowTo',
			'@id': 'https://liftapp.ai/pixverse-ai-video-generator#howto',
			name: 'How it works',
			step: [
				{
					'@type': 'HowToStep',
					position: 1,
					name: 'Describe your video',
					text: 'Type what you want to see — a scene, an ad concept, a short clip idea, anything you can put into words',
				},
				{
					'@type': 'HowToStep',
					position: 2,
					name: 'Pick a model and settings',
					text: 'Choose an AI model, then set your aspect ratio, resolution, and duration — or optionally add a start frame, end frame, or reference image to guide the generation',
				},
				{
					'@type': 'HowToStep',
					position: 3,
					name: 'Let the AI do the work',
					text: 'Watch as PixVerse transforms your text prompt into a unique, high-quality AI-generated video automatically',
				},
				{
					'@type': 'HowToStep',
					position: 4,
					name: 'Download and use',
					text: 'Download your video or publish it straight to your social feeds',
				},
			],
		},
		// benefit
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/pixverse-ai-video-generator#benefits',
			name: 'Who can benefit from the PixVerse AI Video Generator?',
			description:
				"Lift's PixVerse AI Video Generator is useful for micro-agency ad producers, e-commerce demo creators, content creators, marketing teams, and educators who need multi-shot sequences with matching audio from a single description.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Micro-agency ad producers — A full concept usually needs more than one shot to land. PixVerse can generate a multi-shot sequence with matching audio from a single description',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'E-commerce demo creators — Camera moves like tracking shots and perspective shifts help a product feel real, not flat. PixVerse follows that direction while keeping the scene coherent across the move',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Content creators & social media managers — Reels, TikToks, and Shorts need fresh clips fast. Describe the scene you want and get a finished vertical video without filming or editing',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Marketing teams & small business owners — Ad concepts and promo clips with your own product or message come together from a single description, no camera or studio required',
				},
				{
					'@type': 'ListItem',
					position: 5,
					name: 'Educators & storytellers — Scenes, concepts, or scripts that would be hard or costly to film can be generated to match a specific description instead of stock footage',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/benefits.3da6de53.webp',
				name: 'PixVerse AI Video Generator Examples by Lift',
				description:
					'Example of an AI video created with PixVerse using the Lift AI video generator.',
			},
		},

		// faq
		{
			'@type': 'FAQPage',
			'@id': 'https://liftapp.ai/pixverse-ai-video-generator#faq',
			mainEntity: [
				{
					'@type': 'Question',
					name: 'What is PixVerse?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'PixVerse is an AI video model known for generating multi-shot sequences with matching audio in a single pass and for accurate camera movement. Lift uses it to turn a written prompt into a finished video, with PixVerse V6 preselected by default.',
					},
				},
				{
					'@type': 'Question',
					name: 'How does Lift AI Video Generator work?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: "Simply enter your text prompt in Lift's AI video generator, then choose an AI model and adjust settings like ratio, resolution, and duration. Optionally add a start frame, end frame, or reference image to guide the generation. Hit Generate and receive a unique AI-created video in moments — no filming or editing experience needed.",
					},
				},
				{
					'@type': 'Question',
					name: 'Can I use a different AI model besides PixVerse?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: "Yes. Lift's video generator supports a range of AI models, so you can switch between them anytime and choose whichever fits your project best.",
					},
				},
				{
					'@type': 'Question',
					name: 'Can I download the results without a watermark?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: "Absolutely. All videos generated in Lift can be exported without watermark when you're on the Pro plan. Download them in high resolution — ready for social media, ads, or presentations.",
					},
				},
				{
					'@type': 'Question',
					name: 'Is it safe to upload my photos to Lift?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Yes — Lift processes your photos securely and uses them only to generate your results.',
					},
				},
			],
		},

		// why lift
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/pixverse-ai-video-generator#why-lift',
			name: 'Why Choose the PixVerse AI Video Generator by Lift',
			description:
				"Lift's PixVerse AI Video Generator combines professional-grade AI, ease of use, and affordability — making it a trusted choice for millions of users worldwide.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'High-quality results',
					description:
						'AI-powered technology delivers professional-grade visuals every time',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Intelligent rendering',
					description:
						'AI tailors the effect to the scene and subject for optimal results',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Cross-platform support',
					description: 'Available on iOS, Android, and Web for seamless access',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Budget-friendly',
					description:
						'Save on costly designers with an affordable and intuitive tool',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/whyUse.ae83a89c.webp',
				name: 'Why Choose the PixVerse AI Video Generator by Lift',
				description:
					'Lift AI video generator powered by PixVerse — high-quality results, intelligent rendering, cross-platform support, and budget-friendly pricing.',
			},
		},
		// explore
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/pixverse-ai-video-generator#explore-tools',
			name: 'Explore the AI tools',
			description:
				'A curated list of AI-powered tools for image generation, enhancement, background removal, and more – all available on Lift.',
			itemListOrder: 'Unordered',
			...exploreSchemaItemList,
		},

		{
			'@type': 'SoftwareApplication',
			'@id': 'https://liftapp.ai/pixverse-ai-video-generator#all-in-one',
			name: 'Try Lift – AI Content Creation Tools',
			description:
				'Your all-in-one tools to create, edit, and elevate your content. Create stunning content with AI tools, templates, and editing features effortlessly.',
			operatingSystem: 'iOS, Android, Web',
			applicationCategory: 'MultimediaApplication',
			url: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
			applicationSubCategory: 'Content Creation',
			publisher: {
				'@type': 'Organization',
				name: 'Lift',
				url: 'https://liftapp.ai',
			},
			potentialAction: [
				{
					'@type': 'ViewAction',
					name: 'Open Web App',
					target: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
				},
			],
		},

		{
			'@type': 'BreadcrumbList',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Home',
					item: 'https://liftapp.ai',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'PixVerse AI Video Generator',
					item: 'https://liftapp.ai/pixverse-ai-video-generator',
				},
			],
		},
	],
};
